import { useQuery } from 'react-query';
import { api } from '../../../static/api';
import { ApiQuestion, IGameAnswer } from '../../../types/ApiTypes';
import { QueryAliases } from '../../../types/QueryAliases';
import { transformQuestion } from '../../../utils/api/dataTransformers/transformQuestion/transformQuestion';
import useAxiosInstance from '../../axios/useAxiosInstance';

interface ApiUserGame {
  id: number;
  teamName: string | null;
  teamImage: string | null;
  firstQuestionId: number | null;
  points: number;
  route: {
    createdAt: string;
    latitude: string;
    longitude: string;
  }[];
}

interface ApiGameAnswer {
  id: number;
  answerValue: string;
  isChecked: boolean;
  isCorrect: boolean | null;
  question: ApiQuestion;
  userGame: ApiUserGame;
  gainedPoints: number | null;
}

// GMT-795: odpowiedź z page/limit jest stronicowana po grupach pytań
interface ApiPaginatedAnswers {
  data: ApiGameAnswer[];
  count: number;
  totalAnswers: number;
}

interface UseGameReviewAnswersProps {
  gameId: string;
  page?: number;
  limit?: number;
  userGameId?: string;
}

const useGameReviewAnswers = ({ gameId, page, limit, userGameId }: UseGameReviewAnswersProps) => {
  const axiosInstance = useAxiosInstance();

  const { data, refetch, isSuccess, isLoading, isError } = useQuery(
    [QueryAliases.GAME_REVIEW_ANSWERS(gameId), page ?? '', limit ?? '', userGameId ?? ''],
    () =>
      axiosInstance.get(api.endpoints.GAME_REVIEW_ANSWERS({ gameId }), {
        params: { page, limit, userGameId },
      }),
    {
      refetchOnMount: true,
    },
  );

  const apiResponse: ApiPaginatedAnswers | ApiGameAnswer[] | undefined = data?.data;
  const isPaginated = !!apiResponse && !Array.isArray(apiResponse);
  const apiAnswers: ApiGameAnswer[] | undefined = isPaginated
    ? (apiResponse as ApiPaginatedAnswers).data
    : (apiResponse as ApiGameAnswer[] | undefined);

  const iAnswers: IGameAnswer[] | undefined = !!apiAnswers
    ? apiAnswers.map(apiAnswer => {
        const iAnswer: IGameAnswer = {
          id: apiAnswer.id,
          answerValue: apiAnswer.answerValue,
          isChecked: apiAnswer.isChecked,
          isCorrect: apiAnswer.isCorrect,
          question: transformQuestion(apiAnswer.question),
          userGame: {
            id: apiAnswer.userGame.id,
            teamName: apiAnswer.userGame.teamName,
            teamImage: apiAnswer.userGame.teamImage,
            firstQuestionId: apiAnswer.userGame.firstQuestionId,
            points: apiAnswer.userGame.points,
          },
          gainedPoints: apiAnswer.gainedPoints,
        };

        return iAnswer;
      })
    : [];

  const numOfAnswersToCheck = isPaginated
    ? (apiResponse as ApiPaginatedAnswers).totalAnswers
    : iAnswers.filter(answer => !answer.isChecked).length;
  const groupsCount = isPaginated ? (apiResponse as ApiPaginatedAnswers).count : undefined;

  return {
    gameReviewAnswers: iAnswers,
    groupsCount,
    numOfAnswersToCheck,
    refetch,
    isSuccess,
    isLoading,
    isError,
  };
};

export default useGameReviewAnswers;
